import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { BillingSettingsNavigation } from "@/components/pages/billing-settings/components/BillingSettingsNavigation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import { SubscriptionPaymentsFilter } from "@/components/pages/subscription-payments/components/SubscriptionPaymentsFilter";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useDebounce } from "@/hooks/useDebounce";
import { adminSubscriptionTransactionsService } from "@/services/admin-subscription-transactions-service";
import { normalizeSubscriptionTransactionsResponse } from "@/services/subscription-transactions-adapter";
import { tenantSubscriptionService } from "@/services/tenant-subscription-service";
import {
  TenantHotelSubscriptionTransaction,
  TenantHotelSubscriptionTransactionsFilters,
  TenantSubscriptionTransactionStatus,
} from "@/types/tenant-subscription";
import { Role } from "@/types/user";
import {
  hasAnyRole,
  isSuperAdminRole,
  isTenantAdminRole,
} from "@/utils/accessControl";

const PAGE_SIZE = 20;

const STATUS_OPTIONS: TenantSubscriptionTransactionStatus[] = [
  "PENDING",
  "PAID",
  "FAILED",
  "REFUNDED",
];

type TransactionSortField =
  | "hotel"
  | "plan"
  | "status"
  | "amount"
  | "createdAt";
type SortDirection = "asc" | "desc" | null;

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, "dd MMM yyyy HH:mm");
}

function formatMoney(amount: number, currency: string): string {
  const code = currency.trim() === "" ? "USD" : currency.trim().toUpperCase();

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount} ${code}`;
  }
}

function getStatusVariant(status: TenantSubscriptionTransactionStatus) {
  switch (status) {
    case "PAID":
      return "success";
    case "FAILED":
      return "destructive";
    case "REFUNDED":
      return "secondary";
    default:
      return "outline";
  }
}

export const SubscriptionPaymentsPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { userProfile, isLoading: isUserLoading } = useAuthenticatedUser();
  const isTenantAdmin = isTenantAdminRole(userProfile);
  const isSuperAdmin = isSuperAdminRole(userProfile);
  const isHotelAdmin = hasAnyRole(userProfile, [Role.HOTEL_ADMIN]);

  const [hotelName, setHotelName] = useState("");
  const [planTitle, setPlanTitle] = useState("");
  const [status, setStatus] = useState<
    TenantSubscriptionTransactionStatus | "all"
  >("all");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<TransactionSortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const debouncedHotelName = useDebounce(hotelName, 300);
  const debouncedPlanTitle = useDebounce(planTitle, 300);

  const filters = useMemo((): TenantHotelSubscriptionTransactionsFilters => {
    const offset = (currentPage - 1) * PAGE_SIZE;

    return {
      hotelName: debouncedHotelName.trim() || undefined,
      planTitle: debouncedPlanTitle.trim() || undefined,
      status: status === "all" ? undefined : status,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
      limit: PAGE_SIZE,
      offset,
    };
  }, [
    createdFrom,
    createdTo,
    currentPage,
    debouncedHotelName,
    debouncedPlanTitle,
    status,
  ]);

  let transactionScope = "tenant";
  if (isSuperAdmin) {
    transactionScope = "super-admin";
  } else if (isHotelAdmin) {
    transactionScope = "hotel";
  }

  const { data, isLoading } = useQuery({
    queryKey: [
      "tenantHotelSubscriptionTransactions",
      transactionScope,
      filters,
    ],
    queryFn: async () => {
      if (isSuperAdmin) {
        const response =
          await adminSubscriptionTransactionsService.getTransactions(filters);
        return normalizeSubscriptionTransactionsResponse(response, filters);
      }

      return tenantSubscriptionService.getMyHotelsTransactions(filters);
    },
    staleTime: 60 * 1000,
    enabled: !isUserLoading && (isTenantAdmin || isSuperAdmin || isHotelAdmin),
  });

  const rows = useMemo(() => {
    const nextRows = [...(data?.items ?? [])];

    if (sortDirection === null) {
      return nextRows;
    }

    const getSortValue = (
      row: TenantHotelSubscriptionTransaction,
    ): number | string => {
      switch (sortField) {
        case "hotel":
          return row.hotel.name.toLowerCase();
        case "plan":
          return row.plan.title.toLowerCase();
        case "status":
          return row.status;
        case "amount":
          return row.amount;
        case "createdAt":
        default:
          return new Date(row.createdAt).getTime();
      }
    };

    nextRows.sort((left, right) => {
      const leftValue = getSortValue(left);
      const rightValue = getSortValue(right);

      if (leftValue < rightValue) {
        return sortDirection === "asc" ? -1 : 1;
      }

      if (leftValue > rightValue) {
        return sortDirection === "asc" ? 1 : -1;
      }

      return 0;
    });

    return nextRows;
  }, [data?.items, sortDirection, sortField]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const hasActiveFilters =
    hotelName.trim() !== "" ||
    planTitle.trim() !== "" ||
    status !== "all" ||
    createdFrom !== "" ||
    createdTo !== "";

  const handleResetFilters = () => {
    setHotelName("");
    setPlanTitle("");
    setStatus("all");
    setCreatedFrom("");
    setCreatedTo("");
    setCurrentPage(1);
    setSortField("createdAt");
    setSortDirection("desc");
  };

  const columns: Column<TenantHotelSubscriptionTransaction>[] = useMemo(
    () => [
      {
        key: "hotel",
        header: t("billing.subscriptionPayments.table.hotel"),
        sortable: true,
        render: (_: unknown, row) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-zinc-950">
              {row.hotel.name}
            </p>
            <p className="text-muted-foreground truncate text-xs">
              {row.hotel.id}
            </p>
          </div>
        ),
      },
      {
        key: "plan",
        header: t("billing.subscriptionPayments.table.plan"),
        sortable: true,
        render: (_: unknown, row) => row.plan.title,
      },
      {
        key: "status",
        header: t("billing.subscriptionPayments.table.status"),
        className: "text-center",
        sortable: true,
        render: (value: TenantSubscriptionTransactionStatus) => (
          <Badge variant={getStatusVariant(value)}>{value}</Badge>
        ),
      },
      {
        key: "amount",
        header: t("billing.subscriptionPayments.table.amount"),
        className: "text-right",
        sortable: true,
        render: (value: number, row) => formatMoney(value, row.currency),
      },
      {
        key: "stripeInvoiceId",
        header: t("billing.subscriptionPayments.table.invoiceId"),
        render: (value: string, row) =>
          row.invoicePdfUrl ? (
            <a
              href={row.invoicePdfUrl}
              target="_blank"
              rel="noreferrer"
              className="text-primary inline-flex max-w-[220px] items-center gap-1 text-xs underline-offset-4 hover:underline"
            >
              <span className="line-clamp-1">{value}</span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </a>
          ) : (
            <span className="line-clamp-1 max-w-[180px] text-xs">{value}</span>
          ),
      },
      {
        key: "invoicePdfUrl",
        header: t("billing.subscriptionPayments.table.invoicePdf"),
        className: "text-center",
        render: (value: string | null) =>
          value ? (
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              className="text-primary text-sm underline-offset-4 hover:underline"
            >
              {t("billing.subscriptionPayments.table.openInvoice")}
            </a>
          ) : (
            "—"
          ),
      },
      {
        key: "createdAt",
        header: t("billing.subscriptionPayments.table.createdAt"),
        sortable: true,
        render: (value: string) => formatDateTime(value),
      },
    ],
    [t],
  );

  if (!isUserLoading && !isTenantAdmin && !isSuperAdmin && !isHotelAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-6 p-12">
      {isHotelAdmin ? (
        <BillingSettingsNavigation activeTab="transactions" />
      ) : null}

      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-zinc-950">
          {t(
            isHotelAdmin
              ? "billing.subscriptionPayments.hotelTitle"
              : "billing.subscriptionPayments.title",
          )}
        </h1>
        <p className="text-muted-foreground max-w-3xl text-sm">
          {t(
            isHotelAdmin
              ? "billing.subscriptionPayments.hotelDescription"
              : "billing.subscriptionPayments.description",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {t("billing.subscriptionPayments.summary.total", { count: total })}
        </p>
        <div className="flex items-center gap-2">
          <SubscriptionPaymentsFilter
            hotelName={hotelName}
            planTitle={planTitle}
            status={status}
            createdFrom={createdFrom}
            createdTo={createdTo}
            statusOptions={STATUS_OPTIONS}
            onHotelNameChange={(value) => {
              setHotelName(value);
              setCurrentPage(1);
            }}
            onPlanTitleChange={(value) => {
              setPlanTitle(value);
              setCurrentPage(1);
            }}
            onStatusChange={(value) => {
              setStatus(value);
              setCurrentPage(1);
            }}
            onCreatedFromChange={(value) => {
              setCreatedFrom(value);
              setCurrentPage(1);
            }}
            onCreatedToChange={(value) => {
              setCreatedTo(value);
              setCurrentPage(1);
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleResetFilters}
            disabled={!hasActiveFilters}
          >
            {t("billing.subscriptionPayments.filters.reset")}
          </Button>
        </div>
      </div>

      <DataTable<TenantHotelSubscriptionTransaction>
        data={rows}
        columns={columns}
        loading={isLoading || isUserLoading}
        pagination={{
          currentPage,
          totalPages,
          onPageChange: setCurrentPage,
        }}
        sorting={{
          field: sortField,
          direction: sortDirection,
          onSort: (field) => {
            const nextField = field as TransactionSortField;

            if (sortField !== nextField) {
              setSortField(nextField);
              setSortDirection("asc");
              return;
            }

            setSortDirection((currentDirection) => {
              if (currentDirection === "asc") {
                return "desc";
              }

              if (currentDirection === "desc") {
                return null;
              }

              return "asc";
            });
          },
        }}
        emptyMessage={t("billing.subscriptionPayments.table.empty")}
      />
    </div>
  );
};
